import React, { useEffect, useState } from "react";
import SummaryBanner from "./SummaryBanner";
import Form from "../Form";
import UtilityBar from "./UtilityBar";
import JobDatabaseTable from "./JobDatabaseTable";
import MaterialPriceErrorTable from "./MaterialPriceErrorTable";
import ArchiveConfirmModal from "./ArchiveConfirmationModal";
import FilterDrawer from "./FilterDrawer";
import { archiveFormSchema, FILTER_FIELD_NAMES_TO_COUNT } from "./constants";
import JobDatabaseService from "../../services/jobDatabase.service";
import { useJobDatabaseContext } from "../../context/JobDatabaseFilterContext";
import { JobFilterOptionPayloadType } from "../../Types/jobDatabaseTypes";
import { countFiltersApplied } from "../../utils/countAppliedFilters";
import { generatePath, useHistory, useLocation } from "react-router-dom";
import { appRoutes } from "../../constants/appRoutes";

const JobDatabaseListing = () => {
  const navigate = useHistory();
  const location = useLocation();
  const { FetchJobDatabaseSummary, FetchAffectedJobCount } =
    JobDatabaseService();
  const { jobDatabaseFilters, updateJobDatabaseFilters } =
    useJobDatabaseContext();

  const [jobIdValue, setJobIdValue] = useState<string | undefined>();
  const [filterDrawerOpen, setFilterDrawerOpen] = useState<boolean>(false);
  const [archiveConfirmOpen, setArchiveConfirmOpen] = useState<boolean>(false);
  const [sortValue, setSortValue] = useState({});
  const [clearAllFilterButton, setClearAllFilterButton] =
    useState<boolean>(false);
  const isAffectedViewActive = location.hash === "#material-price-error";

  const {
    data: databaseSummary,
    refetch,
    isFetching,
  } = FetchJobDatabaseSummary(jobDatabaseFilters);
  const { data: affectedJobCountResponse, refetch: refetchAffectedJobCount } =
    FetchAffectedJobCount();

  const affectedJobCount = Number(affectedJobCountResponse ?? 0);

  const filterCount = countFiltersApplied(
    jobDatabaseFilters,
    FILTER_FIELD_NAMES_TO_COUNT,
  );

  const handleJobProfileNavigate = (record) => {
    const locationProp = {
      jobName: record?.jobName,
      isBlocked: !!record?.isBlocked,
    };
    navigate.push(
      generatePath(appRoutes.JOB_PROFILE_ID, {
        id: record?.id ?? record?.jobId,
      }),
      locationProp,
    );
  };
  const handleFilterDrawerOpen = () => setFilterDrawerOpen(true);
  const handleFilterDrawerClose = () => setFilterDrawerOpen(false);

  const handleEnableClearAllFilterButton = () => setClearAllFilterButton(true);
  const handleDisableClearAllFilterButton = () =>
    setClearAllFilterButton(false);

  const handleArchiveConfirmOpen = (record?: any) => {
    setJobIdValue(record?.id);
    setArchiveConfirmOpen(true);
  };
  const handleArchiveConfirmClose = () => {
    setJobIdValue(undefined);
    setArchiveConfirmOpen(false);
  };

  const handleSearchAction = (value?: string) => {
    updateJobDatabaseFilters({ search: value });
  };

  const handleSetFilterValues = (values: JobFilterOptionPayloadType) => {
    updateJobDatabaseFilters({ ...values });
  };

  const handleShowBlocked = (checked: boolean) => {
    updateJobDatabaseFilters({ isBlocked: checked });
  };

  const handleToggleAffectedTable = () => {
    navigate.replace({
      pathname: location.pathname,
      hash: isAffectedViewActive ? "" : "material-price-error",
    });
    
    handleSearchAction("");
    handleClearAllFilter();
  };

  const handleAffectedActionComplete = () => {
    refetchAffectedJobCount();
  };

  const handleSorterAction = (sorter) => {
    if (!sorter?.order) {
      setSortValue({
        sortBy: undefined,
        sortOrder: undefined,
      });
      return;
    }
    setSortValue({
      sortBy: sorter?.columnKey,
      sortOrder: sorter?.order,
    });
  };

  const handleClearAllFilter = () => {
    handleEnableClearAllFilterButton();
    updateJobDatabaseFilters({
      jobNumber: [],
      jobName: [],
      companyId: [],
      jobType: [],
      equipment: [],
      materialCategory: [],
      materialType: [],
      salesPersonId: [],
      page: 1,
    });
  };

  useEffect(() => {
    refetch();
  }, [jobDatabaseFilters]);

  return (
    <>
      <SummaryBanner databaseMeta={databaseSummary} loading={isFetching} />
      <Form
        values={{
          searchField: jobDatabaseFilters?.search,
          showBlockedToggle: jobDatabaseFilters?.isBlocked,
        }}
      >
        {() => (
          <UtilityBar
            handleFilterDrawerOpen={handleFilterDrawerOpen}
            handleSetSearchValue={handleSearchAction}
            handleShowBlocked={handleShowBlocked}
            filterCount={filterCount}
            handleClearAllFilter={handleClearAllFilter}
            affectedJobCount={affectedJobCount}
            isAffectedViewActive={isAffectedViewActive}
            handleToggleAffectedView={handleToggleAffectedTable}
          />
        )}
      </Form>
      <Form
        values={{
          jobNumber: jobDatabaseFilters?.jobNumber,
          jobName: jobDatabaseFilters?.jobName,
          companyId: jobDatabaseFilters?.companyId,
          jobType: jobDatabaseFilters?.jobType,
          equipment: jobDatabaseFilters?.equipment,
          materialCategory: jobDatabaseFilters?.materialCategory,
          materialType: jobDatabaseFilters?.materialType,
          salesPersonId: jobDatabaseFilters?.salesPersonId,
        }}
      >
        {() => (
          <FilterDrawer
            visible={filterDrawerOpen}
            handleClose={handleFilterDrawerClose}
            handleSetFilter={handleSetFilterValues}
            clearAllFilterButton={clearAllFilterButton}
            handleClearAllFilter={handleClearAllFilter}
            handleDisableClearAllFilterButton={
              handleDisableClearAllFilterButton
            }
            isAffectedView={isAffectedViewActive}
          />
        )}
      </Form>
      <Form validationSchema={archiveFormSchema}>
        {() => (
          <ArchiveConfirmModal
            isOpen={archiveConfirmOpen}
            handleClose={handleArchiveConfirmClose}
            jobId={jobIdValue}
          />
        )}
      </Form>
      <div>
        {isAffectedViewActive ? (
          <MaterialPriceErrorTable
            jobDatabaseFilters={jobDatabaseFilters}
            handleJobProfileNavigate={handleJobProfileNavigate}
            handleAffectedActionComplete={handleAffectedActionComplete}
          />
        ) : (
          <JobDatabaseTable
            handleJobProfileNavigate={handleJobProfileNavigate}
            handleArchiveConfirmOpen={handleArchiveConfirmOpen}
            searchValue={jobDatabaseFilters?.search}
            jobDatabaseFilters={jobDatabaseFilters}
            jobDatabaseSort={sortValue}
            handleSorterAction={handleSorterAction}
          />
        )}
      </div>
    </>
  );
};

export default JobDatabaseListing;
